import clsx from 'clsx';
import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { MediaGalleryGridItem, type MediaItem } from './Item/MediaGalleryGridItem';
import styles from './MediaGalleryGrid.module.scss';

interface MediaGalleryGridProps {
    maxMobileColumns?: number;
    maxDesktopColumns?: number;
    media: Array<MediaItem>;
    className?: string;
    onClick?: (item: MediaItem) => void;
}

export const MediaGalleryGrid: React.FC<MediaGalleryGridProps> = ({
    media,
    maxMobileColumns = 3,
    maxDesktopColumns = 5,
    className,
    onClick
}) => {
    const isDesktop = useMediaQuery({ minWidth: 1280 });

    const maxColumns = React.useMemo(() => {
        return isDesktop ? maxDesktopColumns : maxMobileColumns;
    }, [isDesktop, maxDesktopColumns, maxMobileColumns]);

    const visibleItems = React.useMemo<Array<MediaItem>>(() => {
        return media.slice(0, maxColumns);
    }, [media, maxColumns]);

    const remainingCount = React.useMemo(() => {
        return Math.max(0, media.length - maxColumns);
    }, [media.length, maxColumns]);

    const shouldShowOverlay = React.useMemo(() => {
        return remainingCount > 0;
    }, [remainingCount]);

    const gridStyle = React.useMemo(
        () =>
            ({
                '--columns': maxMobileColumns,
                '--desktop-columns': maxDesktopColumns
            } as React.CSSProperties),
        [maxMobileColumns, maxDesktopColumns]
    );

    return (
        <div
            className={clsx(styles.root, className)}
            style={gridStyle}
        >
            {visibleItems.map((item, index) => {
                const isLastItem = index === visibleItems.length - 1;
                const showOverlay = shouldShowOverlay && isLastItem;

                return (
                    <MediaGalleryGridItem
                        key={`media-${index}`}
                        item={item}
                        showOverlay={showOverlay}
                        overlayText={`+${remainingCount}`}
                        onClick={onClick}
                    />
                );
            })}
        </div>
    );
};
